import React, { useEffect, useState } from 'react';
import { 
  Modal, 
  Box, 
  Typography, 
  IconButton, 
  Button, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  ListItemSecondaryAction, 
  Divider, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Grid as MuiGrid,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import ShareIcon from '@mui/icons-material/Share';
import CollectionsIcon from '@mui/icons-material/Collections';
import ImageIcon from '@mui/icons-material/Image';
import { ICollection, IAsset } from '../types/damTypes';
import axiosInstance from '../services/axiosInstance';

interface CollectionManagementModalProps {
  open: boolean;
  onClose: () => void;
  collections: ICollection[];
  loading: boolean;
  error: Error | null;
  onCollectionSelected: (collectionId: string | null) => void;
  onCollectionUpdated: () => void; // Callback to refresh collections in the parent
  createOnly?: boolean;
}

interface CollectionFormData {
  name: string;
  description: string;
}

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  border: '1px solid #ccc',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

const CollectionManagementModal: React.FC<CollectionManagementModalProps> = ({
  open,
  onClose,
  collections,
  loading,
  error,
  onCollectionSelected,
  onCollectionUpdated,
  createOnly = false
}) => {
  // State for the collection form
  const [isAddMode, setIsAddMode] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<CollectionFormData>({ name: '', description: '' });
  const [selectedCollection, setSelectedCollection] = useState<ICollection | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState<ICollection | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  useEffect(() => {
    if (!createOnly || !open) return;
    setIsAddMode(true);
    setSelectedCollection(null);
    setFormData({ name: '', description: '' });
  }, [createOnly, open]);

  const dialogOpen = createOnly ? open : formOpen;
  const handleDialogClose = () => {
    if (createOnly) {
      onClose();
      return;
    }
    setFormOpen(false);
  };

  // Handle opening the add collection form
  const handleOpenAddForm = () => {
    setIsAddMode(true);
    setFormData({ name: '', description: '' });
    setFormOpen(true);
  };

  // Handle opening the edit collection form
  const handleOpenEditForm = (collection: ICollection) => {
    setIsAddMode(false);
    setFormData({ 
      name: collection.name, 
      description: collection.description || '' 
    });
    setSelectedCollection(collection);
    setFormOpen(true);
  };

  // Handle form input changes
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      if (isAddMode) {
        // Create a new collection
        await axiosInstance.post(`/api/collections`, formData);
        showSnackbar('Collection created successfully', 'success');
      } else if (selectedCollection) {
        // Update an existing collection
        await axiosInstance.put(`/api/collections/${selectedCollection._id}`, formData);
        showSnackbar('Collection updated successfully', 'success');
      }
      
      if (createOnly) {
        onClose();
      } else {
        setFormOpen(false);
      }
      onCollectionUpdated(); // Refresh collections list
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      showSnackbar(`Error: ${errorMessage}`, 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle opening the delete dialog
  const handleOpenDeleteDialog = (collection: ICollection) => {
    setCollectionToDelete(collection);
    setDeleteDialogOpen(true);
  };

  // Handle deleting a collection
  const handleDeleteCollection = async () => {
    if (!collectionToDelete) return;

    try {
      await axiosInstance.delete(`/api/collections/${collectionToDelete._id}`);
      showSnackbar('Collection deleted successfully', 'success');
      setDeleteDialogOpen(false);
      onCollectionUpdated(); // Refresh collections list
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      showSnackbar(`Error deleting collection: ${errorMessage}`, 'error');
    }
  };

  // Handle sharing a collection
  const handleShareCollection = (collection: ICollection) => {
    // For now, just copy a link to clipboard
    const shareUrl = `${window.location.origin}/shared-collection/${collection._id}`;
    navigator.clipboard.writeText(shareUrl);
    showSnackbar('Collection link copied to clipboard', 'success');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  return (
    <>
      {!createOnly && (
        <Modal
          open={open}
          onClose={onClose}
          aria-labelledby="collection-management-modal-title"
          aria-describedby="collection-management-modal-description"
        >
          <Box sx={style}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography id="collection-management-modal-title" variant="h5" component="h2">
                <CollectionsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Hantera samlingar
              </Typography>
              
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="collection management tabs"
                variant="fullWidth"
              >
                <Tab label="Alla samlingar" id="collections-tab-0" />
                <Tab label="Delat med mig" id="collections-tab-1" disabled />
              </Tabs>
            </Box>

            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={handleOpenAddForm}
              sx={{ mb: 3 }}
            >
              Skapa ny samling
            </Button>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ my: 2 }}>
                Error loading collections: {error.message}
              </Alert>
            ) : collections.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  Inga samlingar hittades. Skapa en ny samling för att komma igång.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {collections.map(collection => (
                  <Box 
                    key={collection._id} 
                    sx={{ 
                      width: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(33.33% - 16px)' } 
                    }}
                  >
                    <Card 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        '&:hover': {
                          boxShadow: 6,
                        }
                      }}
                    >
                      <CardMedia
                        component="div"
                        sx={{
                          height: 140,
                          backgroundColor: 'action.hover',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {(collection.assetCount ?? collection.assets.length) > 0 ? (
                          <ImageIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                        ) : (
                          <FolderIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                        )}
                      </CardMedia>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography gutterBottom variant="h6" component="div" noWrap>
                          {collection.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {collection.assetCount ?? collection.assets.length} {(collection.assetCount ?? collection.assets.length) === 1 ? 'tillgång' : 'tillgångar'}
                        </Typography>
                        {collection.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {collection.description.length > 80 
                              ? `${collection.description.substring(0, 80)}...` 
                              : collection.description}
                          </Typography>
                        )}
                      </CardContent>
                      <CardActions>
                        <Tooltip title="Öppna samling">
                          <Button 
                            size="small" 
                            onClick={() => {
                              onCollectionSelected(collection._id);
                              onClose(); // Stäng modalen när samlingen öppnas
                            }}
                          >
                            Öppna
                          </Button>
                        </Tooltip>
                        <Tooltip title="Redigera samling">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenEditForm(collection)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Dela samling">
                          <IconButton 
                            size="small" 
                            onClick={() => handleShareCollection(collection)}
                          >
                            <ShareIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ta bort samling">
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleOpenDeleteDialog(collection)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    </Card>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Modal>
      )}

      {/* Collection Form Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isAddMode ? 'Skapa ny samling' : 'Redigera samling'}</DialogTitle>
        <Box component="form" onSubmit={handleFormSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Samlingsnamn"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={handleFormChange}
              required
              disabled={formLoading}
            />
            <TextField
              margin="dense"
              name="description"
              label="Beskrivning"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.description}
              onChange={handleFormChange}
              multiline
              rows={3}
              disabled={formLoading}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} disabled={formLoading}>
              Avbryt
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={formLoading || !formData.name.trim()} 
              startIcon={formLoading ? <CircularProgress size={24} /> : null}
            >
              {isAddMode ? 'Skapa' : 'Spara'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Ta bort samling?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Är du säker på att du vill ta bort samlingen "{collectionToDelete?.name}"? 
            Detta kommer att ta bort samlingen, men inte de tillgångar den innehåller.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Avbryt
          </Button>
          <Button onClick={handleDeleteCollection} color="error" variant="contained">
            Ta bort
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={5000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CollectionManagementModal; 